import { useMemo, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../api/client'
import type { Entry } from '../api/entries'
import { EntryCard } from '../components/Entry/EntryCard'
import { usePageHeader } from '../hooks/usePageHeader'
import { useUsername } from '../hooks/useUsername'
import { useAuthStore } from '../store/auth'
import { useAuthorTheme } from '../hooks/useAuthorTheme'

interface TagsetRecord {
  id: string
  name: string
  privacy: number
  tags: string[]
}

export function TagsetView() {
  const username = useUsername()
  const currentUser = useAuthStore((s) => s.username)
  const { tagsetName } = useParams<{ tagsetName: string }>()
  useAuthorTheme(username ?? undefined)
  const [tagset, setTagset] = useState<TagsetRecord | null>(null)
  const [entries, setEntries] = useState<Entry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!username || !tagsetName) return
    setLoading(true)
    const encodedName = encodeURIComponent(tagsetName)
    Promise.all([
      currentUser === username
        ? api.get<TagsetRecord>(`/users/${username}/tagsets/${encodedName}`).catch(() => null)
        : Promise.resolve(null),
      api.get<Entry[]>(`/users/${username}/tagsets/${encodedName}/entries`),
    ])
      .then(([ts, es]) => { setTagset(ts); setEntries(es) })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [username, tagsetName, currentUser])

  const titleNode = useMemo(() => (
    username && tagsetName ? (
      <>
        <Link to={`/~${username}`} style={{ color: 'inherit', textDecoration: 'none', opacity: 0.6 }}>~{username}</Link>
        {' / '}
        <Link to={`/~${username}/tagsets`} style={{ color: 'inherit', textDecoration: 'none', opacity: 0.6 }}>tagsets</Link>
        {` / ${tagsetName}`}
      </>
    ) : 'tagset'
  ), [username, tagsetName])

  usePageHeader(titleNode)

  return (
    <div>
      {tagset && tagset.tags.length > 0 && (
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          {tagset.tags.map((tag) => (
            <span
              key={tag}
              style={{
                background: 'var(--color-fg)', color: 'var(--color-bg)',
                padding: '0.15rem 0.5rem', borderRadius: 2, fontSize: '0.85rem',
              }}
            >
              #{tag}
            </span>
          ))}
        </div>
      )}
      <p style={{ opacity: 0.5, fontSize: '0.85rem', marginBottom: '1rem' }}>
        entries tagged with all tags in this set ·{' '}
        <a href={`/~${username}/tagsets/${tagsetName}.atom`}>atom</a>
      </p>
      {loading ? (
        <p>loading…</p>
      ) : entries.length === 0 ? (
        <p style={{ opacity: 0.5 }}>no entries</p>
      ) : (
        entries.map((e) => (
          <EntryCard
            key={e.id}
            entry={e}
          />
        ))
      )}
    </div>
  )
}
